/**
 * THE PAGES THE RAIL OPENS - one loader each, shared by the two places that
 * need them: `App.tsx` makes each page lazy with it, and `prefetchRailPages`
 * warms every one once the shell is idle.
 *
 * Why the warming matters: a page whose chunk has not arrived SUSPENDS, and
 * the first click on each rail item after every deploy (new chunk hashes) was
 * a network round trip with nothing to show. Warmed, the click is instant.
 */
export const pageLoaders = {
  activity: () => import('@/features/notifications/pages/ActivityPage'),
  chat: () => import('@/features/chat'),
  teams: () => import('@/features/teams'),
  channels: () => import('@/features/channels'),
  calendar: () => import('@/features/calendar'),
  calls: () => import('@/features/calls'),
  recordings: () => import('@/features/meeting/recording'),
  files: () => import('@/features/files'),
  tasks: () => import('@/features/tasks'),
  settings: () => import('@/features/settings'),
} as const;

type IdleWindow = Window & {
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
};

/**
 * Loads every rail page's chunk when the browser is idle. Returns a cancel for
 * an effect cleanup. A failed prefetch costs nothing: the click loads it again.
 */
export function prefetchRailPages(): () => void {
  let cancelled = false;
  const run = (): void => {
    if (cancelled) return;
    for (const load of Object.values(pageLoaders)) void load().catch(() => undefined);
  };
  const idle = window as IdleWindow;
  if (typeof idle.requestIdleCallback === 'function') {
    const handle = idle.requestIdleCallback(run, { timeout: 4_000 });
    return () => {
      cancelled = true;
      idle.cancelIdleCallback?.(handle);
    };
  }
  const timer = window.setTimeout(run, 1_500);
  return () => {
    cancelled = true;
    window.clearTimeout(timer);
  };
}
